import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import * as subApi from '../api/subscriptionApi';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { CreditCard, CheckCircle, AlertCircle, ExternalLink, XCircle, RotateCcw } from 'lucide-react';

const S = {
  header: { marginBottom: 'var(--space-xl)' },
  title: {
    fontFamily: 'var(--font-display)', fontSize: '1.75rem', fontWeight: 700,
    color: 'var(--color-text-primary)', margin: 0, letterSpacing: '0.03em',
  },
  subtitle: { fontSize: '0.9rem', color: 'var(--color-text-muted)', margin: '4px 0 0' },
  centered: { display: 'flex', justifyContent: 'center', padding: 'var(--space-2xl)' },
  message: {
    display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
    padding: 'var(--space-sm) var(--space-md)', borderRadius: 'var(--radius-md)',
    fontSize: '0.85rem', marginBottom: 'var(--space-lg)',
  },
  success: { backgroundColor: 'rgba(46,204,113,0.1)', border: '1px solid rgba(46,204,113,0.2)', color: 'var(--color-success)' },
  warning: { backgroundColor: 'rgba(243,156,18,0.1)', border: '1px solid rgba(243,156,18,0.2)', color: 'var(--color-warning)' },
  planBox: {
    padding: 'var(--space-xl)',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--space-md)',
  },
  price: {
    fontFamily: 'var(--font-display)', fontSize: '3rem', fontWeight: 700,
    color: 'var(--color-primary)', margin: 0, lineHeight: 1,
  },
  priceUnit: { fontSize: '1rem', fontWeight: 400, color: 'var(--color-text-muted)' },
  features: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px', textAlign: 'left' },
  feature: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', color: 'var(--color-text-secondary)' },
  statusRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: 'var(--space-md)', borderRadius: 'var(--radius-md)',
    backgroundColor: 'rgba(212,175,55,0.04)',
  },
  statusLabel: { fontSize: '0.85rem', color: 'var(--color-text-muted)', fontFamily: 'var(--font-display)', letterSpacing: '0.05em', textTransform: 'uppercase' },
  statusValue: { fontSize: '0.9rem', fontWeight: 600, color: 'var(--color-text-primary)' },
  actions: { display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', marginTop: 'var(--space-md)' },
};

const statusLabels = {
  active: 'Attivo',
  canceled: 'Cancellato',
  past_due: 'Pagamento in ritardo',
  incomplete: 'Incompleto',
  none: 'Nessuno',
};

export default function Subscription() {
  const { refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [subData, setSubData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (searchParams.get('success')) {
      setMessage({ type: 'success', text: 'Abbonamento attivato con successo!' });
      refreshUser();
    } else if (searchParams.get('canceled')) {
      setMessage({ type: 'warning', text: 'Checkout annullato.' });
    }
  }, [searchParams, refreshUser]);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await subApi.getSubscriptionStatus();
        setSubData(data);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleCheckout = async () => {
    setActionLoading(true);
    try {
      const { checkout_url } = await subApi.createCheckout();
      window.location.href = checkout_url;
    } catch {
      setMessage({ type: 'warning', text: 'Errore nella creazione del checkout.' });
      setActionLoading(false);
    }
  };

  const handlePortal = async () => {
    setActionLoading(true);
    try {
      const { portal_url } = await subApi.openBillingPortal();
      window.location.href = portal_url;
    } catch {
      setMessage({ type: 'warning', text: 'Errore nell\'apertura del portale.' });
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      const data = await subApi.cancelSubscription();
      setSubData(data);
      setMessage({ type: 'success', text: 'Abbonamento cancellato. Rimarrai attivo fino alla fine del periodo.' });
      refreshUser();
    } catch {
      setMessage({ type: 'warning', text: 'Errore nella cancellazione.' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    setActionLoading(true);
    try {
      const data = await subApi.resumeSubscription();
      setSubData(data);
      setMessage({ type: 'success', text: 'Abbonamento ripristinato!' });
      refreshUser();
    } catch {
      setMessage({ type: 'warning', text: 'Errore nel ripristino.' });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <div style={S.centered}><LoadingSpinner size="lg" /></div>;
  }

  const isSubscribed = subData?.is_subscribed;
  const cancelAtPeriodEnd = subData?.cancel_at_period_end;
  const status = subData?.subscription_status || 'none';

  return (
    <div>
      <div style={S.header}>
        <h1 style={S.title}>Abbonamento</h1>
        <p style={S.subtitle}>Gestisci il tuo piano organizzatore</p>
      </div>

      {message && (
        <div style={{ ...S.message, ...(message.type === 'success' ? S.success : S.warning) }}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      <div style={{ maxWidth: '500px' }}>
        {!isSubscribed && status === 'none' && (
          <Card header="Piano Organizzatore">
            <div style={S.planBox}>
              <div style={S.price}>
                9,99 <span style={S.priceUnit}>/ mese</span>
              </div>

              <ul style={S.features}>
                {[
                  'Partite illimitate',
                  'Schermo live per proiettore/TV',
                  'Premi personalizzabili',
                  'Offerte e scambi del Dottore',
                  'Dashboard e statistiche',
                ].map((f, i) => (
                  <li key={i} style={S.feature}>
                    <CheckCircle size={14} style={{ color: 'var(--color-success)', flexShrink: 0 }} />
                    {f}
                  </li>
                ))}
              </ul>

              {subData?.free_games_remaining > 0 && (
                <div style={{ ...S.message, ...S.success, margin: 0, width: '100%', justifyContent: 'center' }}>
                  <CheckCircle size={14} />
                  Hai ancora {subData.free_games_remaining} partita gratuita!
                </div>
              )}

              <Button
                variant="primary"
                size="lg"
                onClick={handleCheckout}
                loading={actionLoading}
                style={{ width: '100%' }}
              >
                <CreditCard size={18} />
                Abbonati ora
              </Button>
            </div>
          </Card>
        )}

        {isSubscribed && (
          <Card header="Il tuo abbonamento">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
              <div style={S.statusRow}>
                <span style={S.statusLabel}>Stato</span>
                <span style={{
                  ...S.statusValue,
                  color: cancelAtPeriodEnd ? 'var(--color-warning)' : 'var(--color-success)',
                }}>
                  {cancelAtPeriodEnd ? 'In scadenza' : statusLabels[status] || status}
                </span>
              </div>

              {subData.current_period_end && (
                <div style={S.statusRow}>
                  <span style={S.statusLabel}>{cancelAtPeriodEnd ? 'Scade il' : 'Rinnovo'}</span>
                  <span style={S.statusValue}>
                    {new Date(subData.current_period_end).toLocaleDateString('it-IT', {
                      day: 'numeric', month: 'long', year: 'numeric',
                    })}
                  </span>
                </div>
              )}

              <div style={S.statusRow}>
                <span style={S.statusLabel}>Partite create</span>
                <span style={S.statusValue}>{subData.games_count}</span>
              </div>

              <div style={S.actions}>
                <Button variant="secondary" size="sm" onClick={handlePortal} loading={actionLoading}>
                  <ExternalLink size={14} />
                  Gestisci pagamento
                </Button>

                {cancelAtPeriodEnd ? (
                  <Button variant="primary" size="sm" onClick={handleResume} loading={actionLoading}>
                    <RotateCcw size={14} />
                    Riattiva
                  </Button>
                ) : (
                  <Button variant="ghost" size="sm" onClick={handleCancel} loading={actionLoading}>
                    <XCircle size={14} />
                    Cancella
                  </Button>
                )}
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
